import { FC, useState } from 'react';

import './UiShowcase.css';
import Button from './Button';
import Card from './Card';
import CardTitle from './CardTitle';
import ColorField from './Color';
import CurrencyField, { CurrencyInput } from './Currency';
import DangerZone from './DangerZone';
import Dialog from './Dialog';
import EmptyState from './EmptyState';
import Field from './Field';
import FileTrigger from './FileTrigger';
import FormGrid from './FormGrid';
import IconButton from './IconButton';
import { Input, Select, Textarea } from './Inputs';
import Meta from './Meta';
import Notice from './Notice';
import PageContainer from './PageContainer';
import PageHeader from './PageHeader';
import SectionHeader from './SectionHeader';
import Stack from './Stack';
import StatusBadge from './StatusBadge';
import Switch from './Switch';

/**
 * An internal reference surface for the primitives.
 *
 * Development only: reached at #ui, not linked from the navigation. It renders
 * the production primitives exactly as pages consume them - nothing here has its
 * own button or card styling, so a problem in a primitive shows up here rather
 * than being hidden by a showcase-only facade.
 */
const UiShowcase: FC = () => {
  const [on, setOn] = useState(true);
  const [active, setActive] = useState(false);
  const [color, setColor] = useState('#c2185b');
  const [amount, setAmount] = useState('12.50');
  const [uploaded, setUploaded] = useState('no file chosen');
  const [noticeDismissed, setNoticeDismissed] = useState(false);
  const [dialog, setDialog] = useState<'default' | 'danger' | null>(null);

  return (
    <div className="showcase">
      <header className="showcaseHead">
        <h1 className="ui-page-title">UI primitives</h1>
        <p className="ui-page-desc">Every primitive in its states, consumed exactly as a page consumes it.</p>
      </header>

      <section className="showcaseSection">
        <h2 className="ui-section-title">Buttons</h2>
        <Card>
          <div className="showcaseRow">
            <Button variant="primary">Create bundle</Button>
            <Button variant="secondary">Cancel</Button>
            <Button variant="ghost">Edit</Button>
            <Button variant="destructive">Delete bundle</Button>
          </div>
          <div className="showcaseRow">
            <Button variant="primary" size="sm">
              Small
            </Button>
            <Button variant="secondary" size="sm">
              Small
            </Button>
            <Button variant="primary" size="lg">
              Large
            </Button>
          </div>
          <div className="showcaseRow">
            <Button variant="primary" disabled>
              Disabled
            </Button>
            <Button variant="secondary" disabled>
              Disabled
            </Button>
            <Button variant="destructive" disabled>
              Disabled
            </Button>
            <Button variant="primary" block>
              Full width
            </Button>
          </div>
          <div className="showcaseRow">
            <IconButton label="Actions" variant="ghost">
              <span aria-hidden="true">⋯</span>
            </IconButton>
            <IconButton label="Delete" variant="danger">
              <span aria-hidden="true">×</span>
            </IconButton>
            <IconButton label="Disabled action" disabled>
              <span aria-hidden="true">⋯</span>
            </IconButton>
          </div>
        </Card>
      </section>

      <section className="showcaseSection">
        <h2 className="ui-section-title">Fields and controls</h2>
        <Card>
          <div className="showcaseFields">
            <Field label="Plain input" id="sc-plain" description="Helper text sits under the control.">
              <Input placeholder="Placeholder text" />
            </Field>
            <Field label="Populated" id="sc-populated">
              <Input defaultValue="+65 9123 4567" />
            </Field>
            <Field label="Generated id" description="No id prop: React useId() wires the label to the control.">
              <Input placeholder="id is generated" />
            </Field>
            <Field label="Disabled" id="sc-disabled">
              <Input defaultValue="read only" disabled />
            </Field>
            <Field label="Invalid with error" id="sc-invalid" error="Enter a mobile number, e.g. +65 9123 4567.">
              <Input defaultValue="not a number" invalid />
            </Field>
            <Field label="Select" id="sc-select">
              <Select defaultValue="example">
                <option value="example">One</option>
                <option value="two">Two</option>
                <option value="three">Three</option>
              </Select>
            </Field>
            <Field label="Textarea" id="sc-textarea" description="Native element, same shell as the input.">
              <Textarea rows={2} defaultValue="Notes go here." />
            </Field>
            <Field label="Group" group span="full" description="No single control: the label names the group.">
              <div className="showcaseGroup">
                <span className="showcaseChip" />
                <Button size="sm">Choose</Button>
                <Button size="sm" variant="ghost">
                  Remove
                </Button>
              </div>
            </Field>
          </div>
        </Card>
      </section>

      <section className="showcaseSection">
        <h2 className="ui-section-title">Colour</h2>
        <Card>
          <FormGrid columns={2}>
            <ColorField
              id="sc-color"
              label="Accent colour"
              value={color}
              onChange={setColor}
              description="The colour used for the accent."
            />
            <ColorField
              id="sc-color-error"
              label="Border colour"
              value={color}
              onChange={setColor}
              error="Pick a colour that contrasts with the background."
            />
          </FormGrid>
        </Card>
      </section>

      <section className="showcaseSection">
        <h2 className="ui-section-title">Currency input</h2>
        <Card>
          <div className="showcaseFields">
            <CurrencyField
              id="sc-currency"
              label="Price"
              value={amount}
              onChange={(event) => setAmount(event.target.value)}
              description="A decimal text field; the symbol is decorative."
            />
            <CurrencyField
              id="sc-currency-other"
              label="Deposit"
              currency="S$"
              value={amount}
              onChange={(event) => setAmount(event.target.value)}
              description="Another symbol still reads as one control."
            />
            <CurrencyField
              id="sc-currency-error"
              label="Amount due"
              value={amount}
              onChange={(event) => setAmount(event.target.value)}
              error="Enter an amount greater than zero."
            />
            <Field
              id="sc-currency-field"
              label="Field and CurrencyInput"
              description="Field clones the control, so the label and described-by wiring land on the real input."
              error="aria-invalid turns the frame red without an invalid prop."
            >
              <CurrencyInput value={amount} onChange={(event) => setAmount(event.target.value)} />
            </Field>
          </div>
        </Card>
      </section>

      <section className="showcaseSection">
        <h2 className="ui-section-title">Files</h2>
        <Card>
          <Stack direction="row" gap={4} align="center">
            <FileTrigger accept="image/*" onFile={(file) => setUploaded(file.name)}>
              Upload image
            </FileTrigger>
            <FileTrigger accept="image/*" onFile={() => undefined} disabled>
              Upload disabled
            </FileTrigger>
            <Meta>{uploaded}</Meta>
          </Stack>
        </Card>
      </section>

      <section className="showcaseSection">
        <h2 className="ui-section-title">Layout</h2>
        <Card>
          <h3 className="ui-card-title">Stack, row</h3>
          <Stack direction="row" gap={7} align="center" className="showcaseBand">
            <span className="showcaseChip" />
            <span className="showcaseChip" />
            <span className="showcaseChip" />
          </Stack>
          <h3 className="ui-card-title">Stack, column</h3>
          <Stack gap={3} className="showcaseBand">
            <span className="showcaseChip" />
            <span className="showcaseChip" />
            <span className="showcaseChip" />
          </Stack>
          <h3 className="ui-card-title">FormGrid, 2 columns with a full-width field</h3>
          <FormGrid columns={2}>
            <Field id="sc-grid-a" label="First">
              <Input placeholder="one" />
            </Field>
            <Field id="sc-grid-b" label="Second">
              <Input placeholder="two" />
            </Field>
            <Field id="sc-grid-wide" label="Full width" span="full" description='span="full" takes the whole row.'>
              <Input placeholder="spans both columns" />
            </Field>
          </FormGrid>
          <h3 className="ui-card-title">FormGrid, 3 columns</h3>
          <FormGrid columns={3}>
            <Field id="sc-grid-c" label="One">
              <Input />
            </Field>
            <Field id="sc-grid-d" label="Two">
              <Input />
            </Field>
            <Field id="sc-grid-e" label="Three">
              <Input />
            </Field>
          </FormGrid>
          <h3 className="ui-card-title">PageContainer</h3>
          <PageContainer width="standard" className="showcasePage">
            <Meta>standard width: --ui-width-standard plus --ui-page-pad-x each side</Meta>
          </PageContainer>
          <PageContainer width="wide" className="showcasePage">
            <Meta>wide width: --ui-width-wide (capped by the showcase column here)</Meta>
          </PageContainer>
        </Card>
      </section>

      <section className="showcaseSection">
        <h2 className="ui-section-title">Switch</h2>
        <Card>
          <div className="showcaseRow">
            <Switch checked={on} onChange={setOn} label="Shown" />
            <Switch checked={active} onChange={setActive} label="Hidden" />
            <Switch checked disabled onChange={() => undefined} label="Disabled" />
          </div>
          <Meta>Geometry comes from --ui-switch-w/h/pad/thumb/travel.</Meta>
        </Card>
      </section>

      <section className="showcaseSection">
        <h2 className="ui-section-title">Page and section headers</h2>
        <Card>
          <PageHeader
            title="Orders"
            description="Every order taken at the till, newest first."
            action={<Button variant="secondary">Export</Button>}
          />
          <PageHeader title="Orders without an action" description="The header still owns the h1, the description and the space below." />
          <SectionHeader
            title="Account settings"
            description="An embedded section uses an h2 so the page keeps one h1."
            action={
              <Button size="sm" variant="ghost">
                Edit
              </Button>
            }
          />
          <SectionHeader title="Section without an action" description="Same layout as the page header, at the section role." />
        </Card>
      </section>

      <section className="showcaseSection">
        <h2 className="ui-section-title">Notices</h2>
        <Card>
          <Notice variant="error">Settings could not be saved.</Notice>
          <Notice variant="success">Settings saved.</Notice>
          <Notice variant="warning">Enter the value before continuing.</Notice>
          <Notice variant="info">A summary appears in the preview.</Notice>
          {noticeDismissed ? (
            <Meta>The dismissible notice was dismissed.</Meta>
          ) : (
            <Notice variant="info" onDismiss={() => setNoticeDismissed(true)}>
              This notice has a dismiss control.
            </Notice>
          )}
        </Card>
      </section>

      <section className="showcaseSection">
        <h2 className="ui-section-title">Status badges</h2>
        <Card>
          <div className="showcaseRow">
            <StatusBadge tone="success">Active</StatusBadge>
            <StatusBadge tone="neutral">Retired</StatusBadge>
            <StatusBadge tone="warning">Pending</StatusBadge>
            <StatusBadge tone="danger">Failed</StatusBadge>
            <StatusBadge tone="info">Hidden</StatusBadge>
          </div>
          <div className="showcaseRow">
            <StatusBadge tone="success" dot={false}>
              Active
            </StatusBadge>
            <StatusBadge tone="neutral" dot={false}>
              Retired
            </StatusBadge>
            <StatusBadge tone="warning" dot={false}>
              Pending
            </StatusBadge>
            <StatusBadge tone="danger" dot={false}>
              Failed
            </StatusBadge>
            <StatusBadge tone="info" dot={false}>
              Hidden
            </StatusBadge>
          </div>
          <Meta>The dot is decorative; the label and surface carry the tone.</Meta>
        </Card>
      </section>

      <section className="showcaseSection">
        <h2 className="ui-section-title">Empty states</h2>
        <Card>
          <Stack gap={5}>
            <EmptyState title="No discounts yet" description="Create a discount to offer a bundle price at the till." />
            <EmptyState
              title="No payment methods"
              description="Add a payment method before taking a sale."
              action={
                <Button size="sm" variant="secondary">
                  Add payment method
                </Button>
              }
            />
          </Stack>
        </Card>
      </section>

      <section className="showcaseSection">
        <h2 className="ui-section-title">Visually hidden</h2>
        <Card>
          <div className="showcaseRow">
            <span className="ui-visually-hidden">Visually hidden text</span>
            <span aria-hidden="true">(this run has no visible label; inspect the DOM)</span>
            <Meta as="span">.ui-visually-hidden clips a 1px square into the accessibility tree.</Meta>
          </div>
        </Card>
      </section>

      <section className="showcaseSection">
        <h2 className="ui-section-title">Danger zone</h2>
        <Card>
          <DangerZone
            title="Remove integration"
            description="The preview will stop showing this section."
            action={
              <Button variant="destructive" onClick={() => setDialog('danger')}>
                Remove integration
              </Button>
            }
          />
        </Card>
      </section>

      <section className="showcaseSection">
        <h2 className="ui-section-title">Dialog</h2>
        <Card>
          <div className="showcaseRow">
            <Button variant="primary" onClick={() => setDialog('default')}>
              Open dialog
            </Button>
            <Button variant="destructive" onClick={() => setDialog('danger')}>
              Open destructive dialog
            </Button>
          </div>
          <Meta>Escape and a backdrop click close it; focus returns to the button that opened it.</Meta>
        </Card>
        <Dialog
          open={dialog === 'default'}
          onOpenChange={(next) => setDialog(next ? 'default' : null)}
          title="Save these settings?"
          description="The preview updates as soon as you save."
          actions={
            <>
              <Button variant="secondary" onClick={() => setDialog(null)}>
                Cancel
              </Button>
              <Button variant="primary" onClick={() => setDialog(null)}>
                Save
              </Button>
            </>
          }
        >
          <Meta>Any extra content sits between the description and the actions.</Meta>
        </Dialog>
        <Dialog
          open={dialog === 'danger'}
          onOpenChange={(next) => setDialog(next ? 'danger' : null)}
          title="Remove integration?"
          description="The preview will no longer show this section."
          tone="danger"
          actions={
            <>
              <Button variant="secondary" onClick={() => setDialog(null)}>
                Cancel
              </Button>
              <Button variant="destructive" onClick={() => setDialog(null)}>
                Remove
              </Button>
            </>
          }
        />
      </section>

      <section className="showcaseSection">
        <h2 className="ui-section-title">Typography</h2>
        <Card>
          <p className="ui-page-title">Page title</p>
          <p className="ui-page-desc">Page description</p>
          <p className="ui-section-title">Section title</p>
          <p className="ui-card-title">Card title</p>
          <CardTitle level={2}>CardTitle level 2</CardTitle>
          <CardTitle level={3}>CardTitle level 3</CardTitle>
          <CardTitle level={4}>CardTitle level 4</CardTitle>
          <p className="ui-body">Body copy at the standard size.</p>
          <p className="ui-body-sm">Small body copy, used for controls and tables.</p>
          <p className="ui-field-label">Field label</p>
          <Meta>Meta line, the same role as the markup-only .ui-meta.</Meta>
          <p className="ui-metric">$2,711.00</p>
        </Card>
      </section>

      <section className="showcaseSection">
        <h2 className="ui-section-title">Tokens</h2>
        <Card>
          <h3 className="ui-card-title">Colour</h3>
          <div className="showcaseSwatches">
            {[
              'bg',
              'surface',
              'surface-subtle',
              'border',
              'border-strong',
              'text',
              'text-strong',
              'text-title',
              'text-muted',
              'primary',
              'danger',
              'success',
              'warning',
            ].map((name) => (
              <div className="showcaseSwatch" key={name}>
                <span className="showcaseSwatchChip" style={{ backgroundColor: 'var(--ui-' + name + ')' }} />
                <span className="ui-meta">{'--ui-' + name}</span>
              </div>
            ))}
          </div>
          <h3 className="ui-card-title">Spacing</h3>
          <div className="showcaseScale">
            {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11].map((step) => (
              <div className="showcaseScaleRow" key={step}>
                <span className="ui-meta">{'--ui-space-' + step}</span>
                <span className="showcaseScaleBar" style={{ width: 'var(--ui-space-' + step + ')' }} />
              </div>
            ))}
          </div>
          <h3 className="ui-card-title">Radius</h3>
          <div className="showcaseRow">
            {['sm', 'md', 'lg', 'xl', 'pill'].map((size) => (
              <div className="showcaseRadius" key={size} style={{ borderRadius: 'var(--ui-radius-' + size + ')' }}>
                <span className="ui-meta">{size}</span>
              </div>
            ))}
          </div>
        </Card>
      </section>
    </div>
  );
};

export default UiShowcase;
